import { useEffect, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  /** Delay in ms before showing. */
  delay?: number;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Allow the tooltip to wrap and grow wider for multi-line content
   * (e.g. description previews). Default tooltip stays single-line +
   * 260px capped — the existing behavior every other caller wants.
   */
  multiline?: boolean;
}

/**
 * Tooltip rendered via portal with `position: fixed`, so it's never clipped by
 * an ancestor's `overflow: hidden`. The positioning is computed at show time
 * from the wrapping span's bounding rect.
 */
export function Tooltip({ content, children, delay = 300, placement = 'top', multiline = false }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; transform: string; fitted?: boolean } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);

  const compute = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    let top = 0, left = 0, tx = '', ty = '';
    if (placement === 'top') {
      top = r.top - 6; left = r.left + r.width / 2;
      tx = 'translateX(-50%)'; ty = 'translateY(-100%)';
    } else if (placement === 'bottom') {
      top = r.bottom + 6; left = r.left + r.width / 2;
      tx = 'translateX(-50%)';
    } else if (placement === 'left') {
      top = r.top + r.height / 2; left = r.left - 6;
      tx = 'translateX(-100%)'; ty = 'translateY(-50%)';
    } else {
      top = r.top + r.height / 2; left = r.right + 6;
      ty = 'translateY(-50%)';
    }
    setPos({ top, left, transform: `${tx} ${ty}`.trim() });
  };

  const show = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { compute(); setOpen(true); }, delay);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(false);
  };

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // Kept on screen (Jason, 25 Oct: the Performance tab's longer
  // explanations): once drawn, a tip that would leave the window's top or
  // bottom goes to the trigger's other side when there is room there, and
  // one past a side is moved back in. Before paint, so it never shows where
  // it was first put. A tip with no size (nothing laid out) is left alone.
  useLayoutEffect(() => {
    const tip = tipRef.current;
    const trigger = triggerRef.current;
    if (!open || !pos || pos.fitted || !tip || !trigger) return;
    const t = tip.getBoundingClientRect();
    if (t.height === 0) return;
    const r = trigger.getBoundingClientRect();
    const margin = 8;
    let { top, transform } = pos;
    if (placement === 'top' && t.top < margin && r.bottom + 6 + t.height <= window.innerHeight - margin) {
      top = r.bottom + 6;
      transform = transform.replace('translateY(-100%)', '').trim();
    } else if (placement === 'bottom' && t.bottom > window.innerHeight - margin && r.top - 6 - t.height >= margin) {
      top = r.top - 6;
      transform = `${transform} translateY(-100%)`.trim();
    }
    const dx = t.left < margin ? margin - t.left : t.right > window.innerWidth - margin ? window.innerWidth - margin - t.right : 0;
    setPos({ top, left: pos.left + dx, transform, fitted: true });
  }, [open, pos, placement]);

  // The tip is fixed where its trigger was. A scroll (of the page or of a
  // modal's body) moves the trigger from under the pointer without a
  // mouseleave, which would leave the tip floating over unrelated content.
  // Listening from the commit that shows the tip, so no scroll falls between.
  useLayoutEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [open]);

  return (
    <>
      <span
        ref={triggerRef}
        className={styles.wrap}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {open && pos && createPortal(
        <span
          ref={tipRef}
          className={multiline ? `${styles.tip} ${styles.tipMultiline}` : styles.tip}
          style={{ position: 'fixed', top: pos.top, left: pos.left, transform: pos.transform }}
          role="tooltip"
        >
          {content}
        </span>,
        document.body,
      )}
    </>
  );
}
